import { dispatchConnectOpen } from './connect-loader'
import { resolveLandingsCdn } from './config'
import { resolveCategoryLink } from './category'
import { BaseElement, registerCustomElement } from './base-element'

export class EvaroConnectElement extends BaseElement {
  static get observedAttributes(): string[] {
    return ['link', 'vendor', 'category', 'cdn', 'disabled', 'env']
  }

  /** Link resolved from `vendor` + `category`. `null` until resolved, or when unresolvable. */
  private categoryLink: string | null = null
  /** Guards against an out-of-order resolution overwriting a newer one. */
  private resolveSeq = 0
  /** True while this element is hidden because its category did not resolve. */
  private hiddenByCategory = false
  /**
   * Set once `connectedCallback` has run. `attributeChangedCallback` also fires
   * for every attribute present during element upgrade, before connection —
   * this flag suppresses those so the category resolves exactly once on connect.
   */
  private connected = false

  private clickHandler = (event: Event) => {
    if (this.hasAttribute('disabled')) return
    const link = this.getAttribute('link') ?? this.categoryLink
    if (!link) {
      // `link` wins; a `category` may not have resolved yet, or Evaro has not
      // published it. Either way stay silent rather than warn for the category
      // case — a missing link is only a mistake when nothing was set at all.
      if (!this.getAttribute('category')) {
        console.warn('<evaro-connect> click ignored: set `link`, or `vendor` + `category`')
      }
      return
    }
    event.preventDefault()
    dispatchConnectOpen(this, link, this.getAttribute('env') ?? undefined)
  }

  connectedCallback(): void {
    if (!this.hasAttribute('role') && !this.querySelector('a, button')) {
      this.setAttribute('role', 'button')
    }
    if (!this.hasAttribute('tabindex') && !this.querySelector('a, button')) {
      this.setAttribute('tabindex', '0')
    }
    this.style.cursor = this.style.cursor || 'pointer'
    this.addEventListener('click', this.clickHandler)
    this.addEventListener('keydown', this.handleKeydown)
    this.connected = true
    void this.resolveCategory()
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.clickHandler)
    this.removeEventListener('keydown', this.handleKeydown)
    this.connected = false
  }

  attributeChangedCallback(name: string): void {
    if (!this.connected) return
    if (name === 'link' || name === 'vendor' || name === 'category' || name === 'cdn' || name === 'env') {
      void this.resolveCategory()
    }
  }

  /**
   * Resolve `vendor` + `category` to a Connect link. An explicit `link` wins,
   * so resolution is skipped when one is set. The launcher hides until its link
   * is known; a category Evaro has not published stays hidden, raising no error.
   */
  private async resolveCategory(): Promise<void> {
    const category = this.getAttribute('category')
    const vendor = this.getAttribute('vendor')
    if (this.getAttribute('link') || !category || !vendor) {
      this.categoryLink = null
      this.unhideIfHiddenByCategory()
      return
    }
    // Hide until the link is known. A category-driven launcher should not show
    // before it can open anything, and an unprovisioned category never shows.
    this.categoryLink = null
    this.style.display = 'none'
    this.hiddenByCategory = true
    const seq = ++this.resolveSeq
    const cdn = resolveLandingsCdn({ cdn: this.getAttribute('cdn'), env: this.getAttribute('env') })
    const link = await resolveCategoryLink(cdn, vendor, category)
    if (seq !== this.resolveSeq) return // superseded by a newer attribute change
    this.categoryLink = link
    if (link) this.unhideIfHiddenByCategory()
    // A category Evaro has not published: the element stays hidden, no error.
  }

  /** Undo a category-driven hide without touching display the host set itself. */
  private unhideIfHiddenByCategory(): void {
    if (this.hiddenByCategory) {
      this.style.display = ''
      this.hiddenByCategory = false
    }
  }

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.clickHandler(event)
    }
  }
}

export function registerEvaroConnect(): void {
  registerCustomElement('evaro-connect', EvaroConnectElement)
}
